'use client';

import { c as _c } from "react/compiler-runtime";
import { type BackerClaimConfirmation, backerClaimConfirmationKey, CLAIM_RECEIPT_QUERY_KEY, type GetBackerRewardsRow, type ItemsRenderProps, isBackerClaimAwaitingIndex, refetchRealtimeQuery, skipToken, useQueries, useQueryClient } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Skeleton } from '@/components/ui/app-skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { InfiniteList } from '@/components/ui/infinite-list';
import { userPortfolioSnapshotQueryKey } from '@/hooks/positions/user-portfolio/query-key';
import { useUserPortfolioSnapshotFetchedThisSession } from '@/hooks/positions/user-portfolio/query-select';
import { useUserPortfolioPositionRows } from '@/hooks/positions/user-portfolio/selectors';
import { useActiveNetwork } from '@/lib/network/use-active-network';
import { logger } from '@/lib/telemetry/logger';
import { TradingHostProvider } from '@/providers/trading-host-provider';
import { PortfolioProposalCreators } from './portfolio-proposal-creators';
import { isPositiveRewardAmount } from './portfolio-reward-amount';
import { PortfolioBackerRewardItem, PortfolioPrizeRewardItem } from './portfolio-reward-list-item';
import { type PortfolioRewardSource, PortfolioRewardsSources } from './portfolio-rewards-sources';
export type RewardView = 'claimable' | 'claimed';
export interface PortfolioRewardsContent {
  claimableCount: number | undefined;
  claimedCount: number | undefined;
  hasMore: boolean;
  renderItems: (view: RewardView) => ReactNode;
}
export function PortfolioRewards(t0) {
  const $ = _c(14);
  const {
    userId,
    financialQueryId,
    walletPubkeys,
    own,
    children
  } = t0;
  let t1;
  if ($[0] !== financialQueryId || $[1] !== own || $[2] !== walletPubkeys) {
    t1 = own ? [...new Set(walletPubkeys)] : [financialQueryId];
    $[0] = financialQueryId;
    $[1] = own;
    $[2] = walletPubkeys;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== own || $[6] !== userId || $[7] !== walletPubkeys) {
    t2 = sources => <PortfolioRewardsView userId={userId} walletPubkeys={walletPubkeys} own={own} sources={sources}>{children}</PortfolioRewardsView>;
    $[4] = children;
    $[5] = own;
    $[6] = userId;
    $[7] = walletPubkeys;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== own || $[10] !== t1 || $[11] !== t2 || $[12] !== walletPubkeys) {
    t3 = <PortfolioRewardsSources identities={t1} walletPubkeys={walletPubkeys} own={own}>{t2}</PortfolioRewardsSources>;
    $[9] = own;
    $[10] = t1;
    $[11] = t2;
    $[12] = walletPubkeys;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function PortfolioRewardsView(t0) {
  const $ = _c(30);
  const {
    userId,
    walletPubkeys,
    own,
    sources,
    children
  } = t0;
  const t = useTranslations("profile.rewards");
  const queryClient = useQueryClient();
  const network = useActiveNetwork();
  let t1;
  if ($[0] !== userId || $[1] !== walletPubkeys) {
    t1 = {
      userId,
      wallets: walletPubkeys
    };
    $[0] = userId;
    $[1] = walletPubkeys;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const positions = useUserPortfolioPositionRows(t1);
  const fresh = useUserPortfolioSnapshotFetchedThisSession(userId);
  const failed = positions.isError;
  let t2;
  let t3;
  if ($[3] !== failed || $[4] !== own) {
    t2 = () => {
      if (failed) {
        logger.warn("portfolio.rewards.load_failed", {
          source: "prizes",
          viewerMode: own ? "own" : "other"
        });
      }
    };
    t3 = [failed, own];
    $[3] = failed;
    $[4] = own;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  const prizeRows = positions.rows.filter(_temp);
  const claimedPrizes = positions.rows.filter(_temp2);
  let t4;
  if ($[7] !== network) {
    t4 = row_1 => ({
      queryKey: [CLAIM_RECEIPT_QUERY_KEY, row_1.market, row_1.wallet, network],
      queryFn: skipToken,
      gcTime: Infinity,
      staleTime: Infinity
    });
    $[7] = network;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const receipts = useQueries({
    queries: (own ? prizeRows : []).map(t4)
  });
  let t5;
  if ($[9] !== own || $[10] !== receipts) {
    t5 = (_row, index) => !own || receipts[index]?.data !== true;
    $[9] = own;
    $[10] = receipts;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const visiblePrizes = prizeRows.filter(t5);
  const backerCandidates = sources.flatMap(_temp3);
  let t6;
  if ($[12] !== network) {
    t6 = t7 => {
      const {
        row: row_3,
        wallet
      } = t7;
      return {
        queryKey: backerClaimConfirmationKey(network, row_3.marketPubkey, wallet ?? undefined),
        queryFn: skipToken,
        gcTime: Infinity,
        staleTime: Infinity,
        meta: {
          persist: false
        }
      };
    };
    $[12] = network;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const confirmations = useQueries({
    queries: (own ? backerCandidates : []).map(t6)
  });
  let t7;
  if ($[14] !== confirmations || $[15] !== own) {
    t7 = (t8, index_0) => {
      const {
        row: row_4,
        wallet: wallet_0
      } = t8;
      return !own || wallet_0 === null || !isBackerClaimAwaitingIndex(confirmations[index_0]?.data as BackerClaimConfirmation | undefined, {
        marketAddress: row_4.marketPubkey,
        walletAddress: wallet_0,
        userShareD6: row_4.userShareD6,
        claimedTotalD6: row_4.claimedTotalD6
      });
    };
    $[14] = confirmations;
    $[15] = own;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const visibleBackers = backerCandidates.filter(t7);
  const visibleBackersBySource = sources.map(_temp4);
  for (const {
    row: row_5,
    sourceIndex: sourceIndex_0
  } of visibleBackers) {
    visibleBackersBySource[sourceIndex_0]?.add(row_5.marketPubkey);
  }
  let t8;
  if ($[17] !== positions.isPending || $[18] !== sources) {
    t8 = positions.isPending || sources.some(_temp5);
    $[17] = positions.isPending;
    $[18] = sources;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const pending = t8;
  let t9;
  if ($[20] !== failed || $[21] !== sources) {
    t9 = failed || sources.some(_temp6);
    $[20] = failed;
    $[21] = sources;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const error = t9;
  const hasMore = sources.some(_temp7);
  const claimableCount = visiblePrizes.length + visibleBackers.length;
  const claimedCount = claimedPrizes.length + sources.reduce(_temp9, 0);
  let t10;
  if ($[23] !== sources) {
    t10 = async function loadMore() {
      const results = await Promise.allSettled(sources.flatMap(_temp10));
      if (results.some(_temp11)) {
        logger.warn("portfolio.rewards.page_failed", {
          source: "backer_rewards"
        });
      }
    };
    $[23] = sources;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const loadMore = t10;
  let t11;
  if ($[25] !== own || $[26] !== queryClient || $[27] !== sources || $[28] !== userId) {
    t11 = async function retry() {
      const results_0 = await Promise.allSettled([refetchRealtimeQuery(queryClient, userPortfolioSnapshotQueryKey(userId)), ...sources.map(_temp12)]);
      if (results_0.some(_temp13)) {
        logger.warn("portfolio.rewards.retry_failed", {
          viewerMode: own ? "own" : "other"
        });
      }
    };
    $[25] = own;
    $[26] = queryClient;
    $[27] = sources;
    $[28] = userId;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  const retry = t11;
  const renderItems = function renderItems(view) {
    if (pending) {
      return <output aria-label={t("loading")} className="flex flex-col gap-2"><Skeleton className="h-24 w-full rounded-card" /><Skeleton className="h-24 w-full rounded-card" /></output>;
    }
    if (error) {
      return <EmptyState size="compact" title={t("error")} action={<AnimatedButton variant="secondary" onClick={() => void retry()}>{t("retry")}</AnimatedButton>} />;
    }
    const prizes = view === "claimable" ? visiblePrizes : claimedPrizes;
    const count_0 = view === "claimable" ? claimableCount : claimedCount;
    if (count_0 === 0 && !hasMore) {
      return <EmptyState size="compact" title={view === "claimable" ? t("emptyTitle") : t("emptyClaimedTitle")} />;
    }
    const backingPubkeys = [...new Set(sources.flatMap((source_6, index_1) => view === "claimable" ? [...(visibleBackersBySource[index_1] ?? [])] : visibleBackerRewardIds(source_6.query.rows)))];
    const list = <PortfolioProposalCreators userId={userId} pubkeys={backingPubkeys}>{createdIds => <ul className="flex flex-col">{prizes.map(row_7 => <li key={`prize:${row_7.positionId}`} className="mb-4 lg:mb-0"><PortfolioPrizeRewardItem row={row_7} view={view} own={own} fresh={fresh} /></li>)}{sources.map((source_7, index_2) => {
          const visibleIds = view === "claimable" ? visibleBackersBySource[index_2] : new Set(visibleBackerRewardIds(source_7.query.rows));
          const Items = source_7.query.Items;
          return <Items key={source_7.wallet ?? userId} queryKey={source_7.query.queryKey}>{t12 => {
              const {
                id,
                ref
              } = t12;
              return visibleIds?.has(id) ? <li key={id} ref={ref} className="mb-4 lg:mb-0"><PortfolioBackerRewardItem pubkey={id} view={view} wallet={source_7.wallet} fresh={source_7.fresh} isCreator={createdIds.has(id)} /></li> : null;
            }}</Items>;
        })}</ul>}</PortfolioProposalCreators>;
    return <><InfiniteList hasNextPage={hasMore && sources.every(_temp14)} isFetchingNextPage={sources.some(_temp15)} fetchNextPage={loadMore} scrollMode="page-below-lg">{own && view === "claimable" ? <TradingHostProvider>{list}</TradingHostProvider> : list}</InfiniteList>{sources.some(_temp16) ? <p role="alert" className="text-caption text-foreground-secondary">{t("error")}</p> : null}{sources.some(_temp17) ? <div className="flex justify-center"><AnimatedButton variant="secondary" loading={sources.some(_temp18)} disabled={sources.some(_temp19)} onClick={() => void loadMore()}>{t("retry")}</AnimatedButton></div> : null}</>;
  };
  return children({
    claimableCount: pending || error ? undefined : claimableCount,
    claimedCount: pending || error ? undefined : claimedCount,
    hasMore,
    renderItems
  });
}
function _temp19(source_12) {
  return source_12.query.isFetchingNextPage;
}
function _temp18(source_11) {
  return source_11.query.isFetchingNextPage;
}
function _temp17(source_13) {
  return source_13.query.nextPageError !== null;
}
function _temp16(source_10) {
  return source_10.query.nextPageError !== null;
}
function _temp15(source_9) {
  return source_9.query.isFetchingNextPage;
}
function _temp14(source_8) {
  return source_8.query.nextPageError === null;
}
function _temp13(result_0) {
  return result_0.status === "rejected" || result_0.value === "error";
}
function _temp12(source_5) {
  return source_5.query.refetch();
}
function _temp11(result) {
  return result.status === "rejected";
}
function _temp10(source_4) {
  return source_4.query.hasNextPage ? [source_4.query.nextPageError !== null ? source_4.query.retryNextPage() : source_4.query.fetchNextPage()] : [];
}
function _temp9(count, source_3) {
  return count + source_3.query.rows.filter(_temp8).length;
}
function _temp8(row_6) {
  return isPositiveRewardAmount(row_6.claimedTotalD6);
}
function _temp7(source_2) {
  return source_2.query.hasNextPage;
}
function _temp6(source_1) {
  return source_1.query.isError;
}
function _temp5(source_0) {
  return source_0.query.isPending || source_0.query.isPlaceholderData;
}
function _temp4() {
  return new Set();
}
function _temp3(source, sourceIndex) {
  return source.query.rows.flatMap(row_2 => isClaimableBackerReward(row_2) ? [{
    row: row_2,
    wallet: source.wallet,
    sourceIndex
  }] : []);
}
function _temp2(row_0) {
  return isPositiveRewardAmount(row_0.prizeClaimedD6);
}
function _temp(row) {
  return row.prizeState === "claimable" && isPositiveRewardAmount(row.prizeClaimableD6) && isPositiveRewardAmount(row.claimRightsBalanceD6);
}
function visibleBackerRewardIds(rows: readonly GetBackerRewardsRow[]): string[] {
  return rows.flatMap(row => isPositiveRewardAmount(row.claimedTotalD6) ? [row.marketPubkey] : []);
}
function isClaimableBackerReward(row: GetBackerRewardsRow): boolean {
  return row.canClaim && (row.marketState === 'resolved' || row.marketState === 'graduated') && isPositiveRewardAmount(row.earnedD6) && isPositiveRewardAmount(row.userShareD6);
}